import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FlowActions, PdfModal } from '../components'
import { ScrollErrorForm, FileInput, ConfirmationCheckbox } from 'components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { NOTIFICATION_MATERIAL_UNDERTANDING_CONTENT } from 'config/notifications'
import { viewNotificationLetterTemplate, viewNotificationsMaterials } from 'api'
import * as Types from 'types'

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  recordkeeperMaterials: PropTypes.object.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  planStatuses: PropTypes.array.isRequired,
}

const defaultProps = {
  recordkeeperMaterials: [],
}

// If recordkeeper.recordkeeper = "Fidelity.com", this creates a nested schema
// Formik then tries to access form.values["Fidelity"]["com"] instead of form.values["Fidelity.com"]
const sanitizeRecordKeeperPeriod = (name) => (name ?? '').replace(/\./g, '__')

function ReviewNotificationMaterialsForm({
  handleSubmit,
  recordkeeperMaterials,
  planStatuses,
  participantsForSubmission,
}) {
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerUrl, setViewerUrl] = useState('')
  const [viewerTitle, setViewerTitle] = useState('')

  const closeViewer = useCallback(() => {
    setViewerOpen(false)
    if (viewerUrl) {
      URL.revokeObjectURL(viewerUrl)
      setViewerUrl('')
    }
  }, [viewerUrl])
  const norm = (s) => (s ?? '').toString().trim().toLowerCase()
  const uniqBy = (items) => {
    const uniqNames = new Set()
    return items.filter((item) => {
      const k = norm(item?.recordkeeper)
      if (uniqNames.has(k)) return false
      uniqNames.add(k)
      return true
    })
  }
  const RecorkeeperMaterialAvailable = uniqBy(
    participantsForSubmission.filter((p) =>
      recordkeeperMaterials.some((r) => norm(r.Name) === norm(p.recordkeeper))
    )
  )
  const RecordkeeperMaterialNotAvailable = uniqBy(
    participantsForSubmission.filter(
      (p) =>
        !recordkeeperMaterials.some(
          (r) => norm(r.Name) === norm(p.recordkeeper)
        )
    )
  )

  const materialNotpresentCardNumbering =
    RecorkeeperMaterialAvailable?.length > 0 &&
    RecordkeeperMaterialNotAvailable?.length > 0
      ? '4'
      : '3'
  const isTerminatedPlan = planStatuses?.some(
    (recordkeeper) => recordkeeper.planStatus === 'Terminating'
  )
  const isActivePlan = planStatuses?.some(
    (recordkeeper) => recordkeeper.planStatus === 'Active'
  )
  const handleViewLetterTemplate = async (status) => {
    const blob = await viewNotificationLetterTemplate(status)
    const url = URL.createObjectURL(blob)
    setViewerTitle(`${status} plan notification letter template to participant`)
    setViewerUrl(url)
    setViewerOpen(true)
  }
  const handleViewNotificationsMaterials = async (name) => {
    const blob = await viewNotificationsMaterials(name)
    const url = URL.createObjectURL(blob)
    setViewerTitle(
      `${name} plan material to be provided with participant letter`
    )
    setViewerUrl(url)
    setViewerOpen(true)
  }

  return (
    <Formik
      initialValues={{
        uploadLogo: '',
        confirm: false,
        ...RecordkeeperMaterialNotAvailable.reduce((acc, recordkeeper) => {
          acc[sanitizeRecordKeeperPeriod(recordkeeper.recordkeeper)] = ''
          return acc
        }, {}),
      }}
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        confirm: Yup.boolean()
          .required('Required')
          .oneOf([true], 'Check the box to continue'),
        ...RecordkeeperMaterialNotAvailable?.reduce((acc, recordkeeper) => {
          // Temporarily replace . with __ to avoid nested schema issue in Formik
          acc[sanitizeRecordKeeperPeriod(recordkeeper.recordkeeper)] =
            Yup.mixed().required('Upload your document to continue')
          return acc
        }, {}),
      })}
    >
      {({ setFieldValue, setFieldError, setFieldTouched }) => {
        return (
          <ScrollErrorForm>
            <>
              <div className="form-container">
                <div className="card">
                  <h3>1. Review the Inspira letter template(s)</h3>
                  {isActivePlan && (
                    <p>
                      <button
                        type="button"
                        onClick={() => handleViewLetterTemplate('Active')}
                        className="button-text"
                      >
                        Active plan notification letter
                      </button>
                    </p>
                  )}
                  {isTerminatedPlan && (
                    <p>
                      <button
                        type="button"
                        onClick={() => handleViewLetterTemplate('Terminating')}
                        className="button-text"
                      >
                        Terminating plan notification letter
                      </button>
                    </p>
                  )}
                  <PdfModal
                    open={viewerOpen}
                    onClose={closeViewer}
                    url={viewerUrl}
                    title={viewerTitle}
                  />
                  <div>
                    {/* <h6>Upload logo (optional)</h6>
                    <p>
                      If you’d like to add your logo to the template, you can
                      upload one below. View logo guidelines.
                    </p>
                    <FileInput
                      name="uploadLogo"
                      accept={{
                        'image/png': ['.png'],
                        'image/jpg': ['.jpg', '.jpeg'],
                        'image/gif': ['.gif'],
                        'image/svg+xml': ['.svg'],
                      }}
                      subtext={<p>Supports: SVG (preferred), PNG, JPG, GIF </p>}
                      handleError={(message) => {
                        setFieldValue('uploadLogo', '', false)
                        setFieldTouched('uploadLogo', true, false)
                        setFieldError('uploadLogo', message)
                      }}
                      invalidTypeErrorMessage="Only SVG, PNG, JPG, and GIF are supported. Please provide another image."
                      sizeTooLargeErrorMessage="Image is too large. Must be less than 25 MB."
                      invalidPdfPageCountErrorMessage="The number of pages in your file submission is incorrect. Please provide another file."
                      maxSize={25e6}
                    /> */}
                  </div>
                </div>
                {RecorkeeperMaterialAvailable?.length > 0 && (
                  <div className="card">
                    <h3>
                      2. Review and approve the RK provided instruction sheet
                      and Inspira's template 402(f) tax notice which will be
                      part of Plan's notification materials
                    </h3>
                    <p>
                      If you need to add updated materials, reach out to your
                      relationship manager.
                    </p>
                    {RecorkeeperMaterialAvailable.map((recordkeeper, index) => (
                      <>
                        <p key={index + 3}>
                          <button
                            key={index + 4}
                            type="button"
                            onClick={() =>
                              handleViewNotificationsMaterials(
                                recordkeeper.recordkeeper
                              )
                            }
                            className="button-text"
                          >
                            {recordkeeper.recordkeeper}
                          </button>
                        </p>
                      </>
                    ))}
                    <PdfModal
                      open={viewerOpen}
                      onClose={closeViewer}
                      url={viewerUrl}
                      title={viewerTitle}
                    />
                  </div>
                )}
                {RecordkeeperMaterialNotAvailable?.length > 0 && (
                  <div className="card">
                    <h3>
                      {RecordkeeperMaterialNotAvailable?.length > 0 &&
                      RecorkeeperMaterialAvailable?.length === 0
                        ? '2.'
                        : '3.'}{' '}
                      Upload notification materials for the following
                      recordkeeper(s)
                    </h3>
                    <p>
                      Upload any required materials, like election and tax
                      forms, for the following recordkeepers.
                    </p>
                    {RecordkeeperMaterialNotAvailable?.map(
                      (recordkeeper, index) => (
                        <>
                          <h6 key={index}>{recordkeeper.recordkeeper}*</h6>
                          <FileInput
                            key={index + 6}
                            name={sanitizeRecordKeeperPeriod(
                              recordkeeper.recordkeeper
                            )}
                            accept={{
                              'application/pdf': ['.pdf'],
                            }}
                            subtext={<p>Supports: PDF</p>}
                            handleError={(message) => {
                              setFieldValue(
                                sanitizeRecordKeeperPeriod(
                                  recordkeeper.recordkeeper
                                ),
                                '',
                                false
                              )
                              setFieldTouched(
                                sanitizeRecordKeeperPeriod(
                                  recordkeeper.recordkeeper
                                ),
                                true,
                                false
                              )
                              setFieldError(
                                sanitizeRecordKeeperPeriod(
                                  recordkeeper.recordkeeper
                                ),
                                message
                              )
                            }}
                            invalidTypeErrorMessage="Only PDFs are supported. Please provide another document."
                            sizeTooLargeErrorMessage="File must be 15MB or less."
                            invalidPdfPageCountErrorMessage="The number of pages in your file submission is incorrect. Please provide another file."
                            maxSize={15e6}
                          />
                        </>
                      )
                    )}
                  </div>
                )}
                <div className="card">
                  <h3>
                    {materialNotpresentCardNumbering}. Acknowledge the following
                  </h3>
                  <p>
                    Check the box to confirm you have read and agree to the
                    terms.
                  </p>
                  <ConfirmationCheckbox
                    name="confirm"
                    label={NOTIFICATION_MATERIAL_UNDERTANDING_CONTENT}
                    errorText="Check the box to continue"
                  />
                </div>
              </div>
            </>
            <FlowActions submitContent="Continue" />
          </ScrollErrorForm>
        )
      }}
    </Formik>
  )
}

ReviewNotificationMaterialsForm.propTypes = propTypes
ReviewNotificationMaterialsForm.defaultProps = defaultProps

export default ReviewNotificationMaterialsForm
